import { abrePDF } from "../../utils/pdf";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { fDMA } from "../../utils/fecha";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  Label,
  ButtonGroup,
  Col,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../DataTable";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import moment from "moment";

/**
 * Reporte combinado de VENTAS y COTIZACIONES PENDIENTES.
 *
 *   Ventas       = surtidos activos, por su importe COMPLETO.
 *   Cotizaciones = pedidos con autorizadoSurtido = "Si" que todavía NO se
 *                  surten (surtido !== "Si") y siguen abiertos.
 *
 * Ninguna de las dos incluye canceladas. Se excluye la cotización ya surtida
 * porque su importe ya viene contado en el renglón de Venta: dejarla sumaría
 * el mismo dinero dos veces. Las cerradas tampoco salen: ya no son un
 * compromiso pendiente. Así el total es "vendido + comprometido".
 *
 * Por construcción todo renglón está autorizado a surtir, así que no hay
 * columna ni filtro de "Aut. Surtido": sería constante.
 *
 * Las dos fuentes se normalizan al mismo renglón para poder ordenarlas,
 * filtrarlas y sumarlas juntas. El PDF, el Excel y los totales consumen el
 * listado ya filtrado (datosFiltrados), no una copia aparte de los filtros:
 * así no se pueden desfasar entre sí.
 */
function ReporteVentasCotizaciones() {
  const { user } = useContext(AuthContext);
  const URL_PEDIDOS = process.env.REACT_APP_URL_PEDIDOS;
  const URL_SURTIDOS = process.env.REACT_APP_URL_SURTIDOS;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
  const URL_COLABORADORES = process.env.REACT_APP_URL_COLABORADORES;

  let hoy = new Date();
  let inicio = new Date();
  inicio.setDate(inicio.getDate() - 122);

  let endDate = moment(hoy).format("YYYY-MM-DD");
  let startDate = moment(inicio).format("YYYY-MM-DD");

  const [clientes, setClientes] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedColaborador, setSelectedColaborador] = useState("");
  const [tipo, setTipo] = useState("");
  const [status, setStatus] = useState("");

  const [comments, setComments] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 50;

  const headers = [
    { name: "Tipo", field: "tipo", sortable: true },
    { name: "Numero", field: "numero", sortable: true },
    { name: "Fecha", field: "fecha", sortable: true },
    { name: "Cotizacion", field: "pedido", sortable: true },
    { name: "Cliente", field: "clientes", sortable: true },
    { name: "Vendedor", field: "colaboradores", sortable: true },
    { name: "Piezas", field: "total_cantidad", sortable: true },
    { name: "Status", field: "status", sortable: true },
    { name: "SubTotal", field: "subTotal", sortable: true },
    { name: "IVA", field: "iva", sortable: true },
    { name: "Total", field: "total_general", sortable: true },
  ];

  useMemo(() => {
    showLoader();
    const headersAuth = {
      headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
    };

    Promise.all([
      axios.get(
        `${URL_PEDIDOS}Fechas/${selectedFechaInicio}/${selectedFechaFin}`,
        headersAuth
      ),
      axios.get(
        `${URL_SURTIDOS}Fechas/${selectedFechaInicio}/${selectedFechaFin}`,
        headersAuth
      ),
    ])
      .then(([resPedidos, resSurtidos]) => {
        let allPedidos = resPedidos.data || [];
        let allSurtidos = resSurtidos.data || [];

        // Un vendedor sólo ve lo suyo, igual que en ListadoPedidos
        if (user.vendedor === "Si") {
          allPedidos = allPedidos.filter(
            (p) => p.colaboradores?.[0]?.email === user.email
          );
          allSurtidos = allSurtidos.filter(
            (s) => s.pedidos?.[0]?.colaboradores?.[0]?.email === user.email
          );
        }

        // COTIZACIONES: autorizadas a surtir, que TODAVÍA no se surten, ni
        // canceladas ni cerradas. Las ya surtidas se excluyen porque su
        // importe ya viene contado en el renglón de Venta: si no, se sumaría
        // dos veces. Las cerradas tampoco: ya no son compromiso pendiente.
        const filaPedidos = allPedidos
          .filter(
            (a) =>
              a.autorizadoSurtido === "Si" &&
              a.surtido !== "Si" &&
              a.is_active !== "No" &&
              a.status !== "Cerrado"
          )
          .map((a) => {
            const cli = a.clientes?.[0] || {};
            const colab = a.colaboradores?.[0] || {};
            return {
              _id: a._id,
              tipo: "Cotizacion",
              numero: a.idPedido,
              pedido: a.idPedido,
              fecha: a.fecha,
              activo: a.is_active,
              status: a.status || "Abierto",
              autorizadoSurtido: a.autorizadoSurtido || "No",
              clientes: cli.nombre_comercial || "NA",
              idClientes: cli._id || "",
              colaboradores:
                (colab.nombre || "Sin") + " " + (colab.apellido || "vendedor"),
              idColaboradores: colab._id || "",
              total_cantidad: Number(a.total_cantidad) || 0,
              subTotal: Number(a.subTotal) || 0,
              iva: Number(a.iva) || 0,
              total_general: Number(a.total_general) || 0,
            };
          });

        // VENTAS: los surtidos activos, por su importe completo
        const filaSurtidos = allSurtidos
          .filter((a) => a.is_active !== "No")
          .map((a) => {
            const ped = a.pedidos?.[0] || {};
            const cli = a.clientes?.[0] || ped.clientes?.[0] || {};
            const pedCli = ped.clientes?.[0] || {};
            const colab = ped.colaboradores?.[0] || {};
            return {
              _id: a._id,
              tipo: "Venta",
              numero: a.idSurtido,
              pedido: ped.idPedido || "",
              fecha: a.fecha,
              activo: a.is_active,
              // Una venta ya surtida cuenta como cerrada; así el filtro de
              // status se comporta igual que en ListadoPedidos.
              status: "Cerrado",
              autorizadoSurtido: "Si",
              clientes: pedCli.nombre_comercial || cli.nombre_comercial || "NA",
              idClientes: pedCli._id || cli._id || "",
              colaboradores:
                (colab.nombre || "Sin") + " " + (colab.apellido || "vendedor"),
              idColaboradores: colab._id || "",
              total_cantidad: Number(a.total_cantidad) || 0,
              subTotal: Number(a.subTotal) || 0,
              iva: Number(a.iva) || 0,
              total_general: Number(a.total_general) || 0,
            };
          });

        setComments([...filaSurtidos, ...filaPedidos]);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  }, [user, selectedFechaInicio, selectedFechaFin]);

  useEffect(() => {
    const headersAuth = {
      headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
    };
    axios
      .get(`${URL_CLIENTES}GeneralActivos`, headersAuth)
      .then((response) => setClientes(response.data || []))
      .catch((err) => console.log(err));

    axios
      .get(URL_COLABORADORES, headersAuth)
      .then((response) => setColaboradores(response.data || []))
      .catch((err) => console.log(err));
  }, []);

  const commentsData = useMemo(() => {
    let computedComments = comments;

    if (search) {
      computedComments = computedComments.filter(
        (c) =>
          c.colaboradores.toLowerCase().includes(search.toLowerCase()) ||
          c.clientes.toLowerCase().includes(search.toLowerCase()) ||
          c.total_cantidad.toString().includes(search) ||
          c.numero.toString().includes(search)
      );
    }
    if (tipo) {
      computedComments = computedComments.filter((e) => e.tipo === tipo);
    }
    if (selectedCliente) {
      computedComments = computedComments.filter((e) =>
        e.idClientes.includes(selectedCliente)
      );
    }
    if (selectedColaborador) {
      computedComments = computedComments.filter((e) =>
        e.idColaboradores.includes(selectedColaborador)
      );
    }
    if (status) {
      computedComments = computedComments.filter((e) =>
        (e.status || "").includes(status)
      );
    }
    if (selectedFechaInicio) {
      computedComments = computedComments.filter(
        (e) => e.fecha >= selectedFechaInicio
      );
    }
    if (selectedFechaFin) {
      computedComments = computedComments.filter(
        (e) => e.fecha <= selectedFechaFin
      );
    }

    setTotalItems(computedComments.length);

    // Ordenamiento: los campos numéricos no tienen localeCompare
    const camposNumericos = [
      "numero",
      "pedido",
      "total_cantidad",
      "subTotal",
      "iva",
      "total_general",
    ];
    if (sorting.field) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      if (camposNumericos.includes(sorting.field)) {
        computedComments = [...computedComments].sort(
          (a, b) =>
            reversed *
            ((parseFloat(a[sorting.field]) || 0) -
              (parseFloat(b[sorting.field]) || 0))
        );
      } else {
        computedComments = [...computedComments].sort(
          (a, b) =>
            reversed *
            String(a[sorting.field] ?? "").localeCompare(
              String(b[sorting.field] ?? "")
            )
        );
      }
    }

    // Listado COMPLETO ya filtrado y ordenado (sin paginar): lo consumen el
    // PDF, el Excel y los totales.
    setDatosFiltrados(computedComments);

    return computedComments.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [
    comments,
    currentPage,
    search,
    sorting,
    tipo,
    selectedCliente,
    selectedColaborador,
    status,
    selectedFechaInicio,
    selectedFechaFin,
  ]);

  // === Totales del listado filtrado ===
  // El reporte ya excluye canceladas y cotizaciones surtidas, así que todo
  // renglón que llega aquí suma: las ventas por su importe completo.
  const vivas = datosFiltrados || [];
  const sumar = (campo) =>
    vivas.reduce((t, a) => t + (Number(a[campo]) || 0), 0);
  const totPiezas = sumar("total_cantidad");
  const totSubTotal = sumar("subTotal");
  const totIva = sumar("iva");
  const totGeneral = sumar("total_general");
  const totVentas = vivas.filter((a) => a.tipo === "Venta");
  const totCotiz = vivas.filter((a) => a.tipo === "Cotizacion");
  const sumaDe = (arr, campo) =>
    arr.reduce((t, a) => t + (Number(a[campo]) || 0), 0);

  const money = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n) || 0);
  const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);

  function PDFTabla() {
    const dataPDF = datosFiltrados.map((a) => [
      a.tipo,
      a.numero,
      a.fecha,
      a.pedido,
      a.clientes,
      a.colaboradores,
      num(a.total_cantidad),
      a.status,
      money(a.subTotal),
      money(a.iva),
      money(a.total_general),
    ]);

    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 120, 10, 35, 18);

    doc.setFontSize(12);
    doc.text(`Ventas y Cotizaciones`, 14, 30);
    doc.setFontSize(9);
    doc.text(
      `${selectedFechaInicio} - ${selectedFechaFin}   ·   Registros: ${datosFiltrados.length}`,
      14,
      37
    );
    doc.text(
      `Ventas: ${totVentas.length} por ${money(sumaDe(totVentas, "total_general"))}   ·   Cotizaciones autorizadas: ${totCotiz.length} por ${money(sumaDe(totCotiz, "total_general"))}`,
      14,
      43
    );

    doc.autoTable({
      head: [
        [
          "Tipo",
          "Numero",
          "Fecha",
          "Cotizacion",
          "Cliente",
          "Vendedor",
          "Piezas",
          "Status",
          "SubTotal",
          "IVA",
          "Total",
        ],
      ],
      body: dataPDF,
      foot: [
        [
          "",
          "",
          "",
          "",
          "",
          "TOTALES",
          num(totPiezas),
          "",
          money(totSubTotal),
          money(totIva),
          money(totGeneral),
        ],
      ],
      headStyles: {
        fillColor: ["#911c04"],
        lineWidth: 0,
        fontSize: 7,
        halign: "center",
      },
      footStyles: { fillColor: ["#911c04"], fontSize: 7, halign: "right" },
      bodyStyles: { fontSize: 7 },
      columnStyles: {
        6: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "right" },
      },
      theme: "grid",
      startY: 48,
      margin: { left: 8, right: 8 },
    });

    abrePDF(doc, `VentasYCotizaciones.pdf`);
  }

  function excel() {
    const dataExcel = datosFiltrados.map((a) => ({
      Tipo: a.tipo,
      Numero: a.numero,
      Fecha: a.fecha,
      Cotizacion: a.pedido,
      Cliente: a.clientes,
      Vendedor: a.colaboradores,
      Piezas: Number(a.total_cantidad) || 0,
      Status: a.status,
      SubTotal: Number(a.subTotal) || 0,
      IVA: Number(a.iva) || 0,
      Total: Number(a.total_general) || 0,
    }));

    dataExcel.push({
      Tipo: "",
      Numero: "",
      Fecha: "",
      Cotizacion: "",
      Cliente: "",
      Vendedor: "TOTALES",
      Piezas: totPiezas,
      Status: "",
      SubTotal: totSubTotal,
      IVA: totIva,
      Total: totGeneral,
    });

    const columnas = [
      "Tipo",
      "Numero",
      "Fecha",
      "Cotizacion",
      "Cliente",
      "Vendedor",
      "Piezas",
      "Status",
      "SubTotal",
      "IVA",
      "Total",
    ];

    var option = {};
    option.fileName = `VentasYCotizaciones-${selectedFechaInicio}-${selectedFechaFin}`;
    option.datas = [
      {
        sheetData: dataExcel,
        sheetName: "VentasYCotizaciones",
        sheetFilter: columnas,
        sheetHeader: [
          "Tipo",
          "Numero",
          "Fecha",
          "Cotizacion",
          "Cliente",
          "Vendedor",
          "Piezas",
          "Status",
          "SubTotal",
          "IVA",
          "Total",
        ],
      },
    ];
    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_ventas ? (
        <div className="card container col-12">
          <Row>
            <Col sm={8}>
              <Button
                size="sm"
                href="/MenuVentas"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Ventas y Cotizaciones</h3>

          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
            <div className="col-md-4 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <Row>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                required
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                required
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Tipo</Label>
              <Input
                bsSize="sm"
                type="select"
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                <option value="Venta">Ventas</option>
                <option value="Cotizacion">Cotizaciones</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label>Status</Label>
              <Input
                bsSize="sm"
                type="select"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                <option value="Abierto">Abierto</option>
                <option value="Cerrado">Cerrado</option>
              </Input>
            </Col>
          </Row>
          <br />

          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader
              headers={headers}
              onSorting={(field, order) => setSorting({ field, order })}
            />
            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td style={{ paddingTop: "0px" }}>
                <Input
                  bsSize="sm"
                  type="select"
                  value={selectedCliente}
                  onChange={(e) => {
                    setSelectedCliente(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Selecciona</option>
                  {clientes
                    .sort((a, b) =>
                      a.nombre_comercial > b.nombre_comercial ? 1 : -1
                    )
                    .map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.nombre_comercial}
                      </option>
                    ))}
                </Input>
              </td>
              <td style={{ paddingTop: "0px" }}>
                <Input
                  bsSize="sm"
                  type="select"
                  value={selectedColaborador}
                  onChange={(e) => {
                    setSelectedColaborador(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Selecciona un Vendedor</option>
                  {colaboradores
                    .sort((a, b) => (a.nombre > b.nombre ? 1 : -1))
                    .map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.nombre} {a.apellido}
                      </option>
                    ))}
                </Input>
              </td>
            </tr>
            <tbody>
              {commentsData.map((a) => {
                return (
                  <tr key={`${a.tipo}-${a._id}`}>
                    <td>
                      <span
                        style={{
                          color: a.tipo === "Venta" ? "#155724" : "#856404",
                          fontWeight: "bold",
                        }}
                      >
                        {a.tipo}
                      </span>
                    </td>
                    <td>{a.numero}</td>
                    <td>{fDMA(a.fecha)}</td>
                    <td>{a.pedido}</td>
                    <td>{a.clientes}</td>
                    <td>{a.colaboradores}</td>
                    <td>{num(a.total_cantidad)}</td>
                    <td>{a.status}</td>
                    <td align="right">{money(a.subTotal)}</td>
                    <td align="right">{money(a.iva)}</td>
                    <td align="right">{money(a.total_general)}</td>
                  </tr>
                );
              })}
              {/* Totales del listado filtrado (excluye canceladas) */}
              <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                <td colSpan={6} align="right">
                  TOTALES ({totVentas.length} ventas · {totCotiz.length} cotizaciones)
                </td>
                <td>{num(totPiezas)}</td>
                <td></td>
                <td align="right">{money(totSubTotal)}</td>
                <td align="right">{money(totIva)}</td>
                <td align="right">{money(totGeneral)}</td>
              </tr>
            </tbody>
          </Table>

          <div className="col-md-6">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
          <br />
          <br />
        </div>
      ) : undefined}
      {loader}
    </>
  );
}

export default ReporteVentasCotizaciones;
